import React, { useState, useEffect, useRef } from 'react'; 
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import './GlobalStyles.css';
import forkliftImg from './Assets/forklift.png';

const CreateShippingItem = ({ userName }) => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Form fields
  const [shippingID, setShippingID] = useState('');
  const [qtyInLot, setQtyInLot] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [projectManager, setProjectManager] = useState('');
  const [company, setCompany] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shipper, setShipper] = useState('');
  const [description, setDescription] = useState('');
  const [rcvdDate, setRcvdDate] = useState('');
  const [receiver, setReceiver] = useState(userName);
  // New state for Email Procurement checkbox
  const [emailProcurement, setEmailProcurement] = useState(false);

  // Loading and success states
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdShippingID, setCreatedShippingID] = useState(null);

  // Determine if user is on mobile (screen width < 700)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Shipping Photo States ---
  const [photo, setPhoto] = useState(null); // preview URL
  const [nativePhotoFile, setNativePhotoFile] = useState(null);
  // Reference for mobile hidden input (only used on mobile)
  const photoInputRef = useRef(null);

  // --- Manifest Document States ---
  // Mobile: scanned pages and cropper for converting to PDF
  const [scannedPages, setScannedPages] = useState([]); // array of data URLs
  const [capturedScan, setCapturedScan] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  // Desktop: single PDF file upload
  const [pdfManifest, setPdfManifest] = useState(null);
  // Reference for mobile hidden input (only used on mobile)
  const scanInputRef = useRef(null);
  // Cropper ref (mobile only)
  const cropperRef = useRef(null);

  useEffect(() => {
    setReceiver(userName);
  }, [userName]);

  // --- Fetch Project Details ---
  const fetchProjectDetails = async () => {
    if (projectNumber) {
      setIsProjectLoading(true);
      const baseProjectNumber = projectNumber.split('.')[0];
      let lookupProjectNumber = baseProjectNumber;
      if (/^pn/i.test(baseProjectNumber)) {
        lookupProjectNumber = baseProjectNumber.slice(2);
      }
      try {
        const response = await axios.get(`${apiBaseUrl}/api/sesd365/data`, {
          params: { projectNumber: lookupProjectNumber },
        });
        if (response.data && response.data.length > 0) {
          setProjectManager(response.data[0].ProjectManager_NameAlias);
          setCompany(response.data[0].CustomerName);
        } else {
          setProjectManager('');
          setCompany('');
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
      } finally {
        setIsProjectLoading(false);
      }
    }
  };

  // --- Shipping Photo Handlers ---
  const handleTakePhoto = () => {
    if (isMobile && photoInputRef.current) {
      photoInputRef.current.value = null;
      photoInputRef.current.click();
    }
  };

  const handlePhotoSelected = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPhoto(previewUrl);
      setNativePhotoFile(file);
    }
  };

  const handleRetakePhoto = () => {
    setPhoto(null);
    setNativePhotoFile(null);
  };

  // --- Manifest Document Handlers ---
  // For mobile: scan document using native file input
  const handleScanDocument = () => {
    if (isMobile && scanInputRef.current) {
      scanInputRef.current.value = null;
      scanInputRef.current.click();
    }
  };

  const handleScanSelected = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setCapturedScan(previewUrl);
      setShowCropper(true);
      setIsProcessingScan(true);
      try {
        const enhanced = await applyContrastEnhancement(previewUrl, 150);
        setCapturedScan(enhanced);
      } catch (error) {
        console.error('Contrast enhancement failed:', error);
      }
      setIsProcessingScan(false);
    }
  };

  // Desktop: handle PDF manifest file change
  const handlePDFManifestChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdfManifest(file);
    }
  };

  // Contrast Enhancement (using canvas)
  const applyContrastEnhancement = (imageDataUrl, contrast = 150) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.filter = `contrast(${contrast}%)`;
        ctx.drawImage(image, 0, 0, image.width, image.height);
        resolve(canvas.toDataURL('image/jpeg', 1));
      };
      image.onerror = reject;
      image.src = imageDataUrl;
    });
  };

  // Cropper handlers for mobile document scan
  const handleAddScannedPage = () => {
    if (cropperRef.current) {
      const croppedDataUrl = cropperRef.current.cropper
        .getCroppedCanvas()
        .toDataURL('image/jpeg', 1);
      setScannedPages((prev) => [...prev, croppedDataUrl]);
      setShowCropper(false);
      setCapturedScan(null);
    }
  };

  const handleDoneCropping = () => {
    if (cropperRef.current) {
      const croppedDataUrl = cropperRef.current.cropper
        .getCroppedCanvas()
        .toDataURL('image/jpeg', 1);
      setScannedPages((prev) => [...prev, croppedDataUrl]);
    }
    setShowCropper(false);
    setCapturedScan(null);
  };

  const handleClearScannedPages = () => {
    setScannedPages([]);
  };

  // --- Form Submission ---
  const handleSave = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const formData = new FormData();
    formData.append('ShippingID', shippingID);
    formData.append('QtyInLot', qtyInLot);
    formData.append('ProjectNumber', projectNumber);
    formData.append('ProjectManager', projectManager);
    formData.append('Company', company);
    formData.append('Carrier', carrier);
    formData.append('Shipper', shipper);
    formData.append('Description', description);
    formData.append('Rcvd_Date', rcvdDate);
    formData.append('Receiver', receiver);
    // Append manifestOption based on the Email Procurement checkbox
    formData.append('manifestOption', emailProcurement ? 'Procurement' : '');

    // Append Shipping Photo
    if (nativePhotoFile) {
      formData.append('photo', nativePhotoFile);
    } else if (photo && typeof photo === 'string') {
      const blob = await fetch(photo).then((res) => res.blob());
      formData.append('photo', blob, 'captured_photo.jpg');
    }

    // Append Manifest Document:
    // Mobile: convert scanned pages to PDF; Desktop: use uploaded PDF file
    if (isMobile) {
      if (scannedPages.length > 0) {
        try {
          const { PDFDocument } = await import('pdf-lib');
          const pdfDoc = await PDFDocument.create();
          for (let pageDataUrl of scannedPages) {
            const imageBytes = await fetch(pageDataUrl).then((res) => res.arrayBuffer());
            const jpgImage = await pdfDoc.embedJpg(imageBytes);
            const { width, height } = jpgImage.scale(1);
            const pdfPage = pdfDoc.addPage([width, height]);
            pdfPage.drawImage(jpgImage, { x: 0, y: 0, width, height });
          }
          const pdfBytes = await pdfDoc.save();
          const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
          formData.append(
            'manifestPhoto',
            pdfBlob,
            scannedPages.length > 0 ? 'scanned_document.pdf' : 'manifest.pdf'
          );
        } catch (error) {
          console.error('Error creating PDF from scanned pages:', error);
        }
      }
    } else {
      if (pdfManifest) {
        formData.append('manifestPhoto', pdfManifest);
      }
    }

    try {
      await axios.post(`${apiBaseUrl}/api/shipping-items`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCreatedShippingID(shippingID);
      setIsSuccess(true);
    } catch (error) {
      console.error('Error creating shipping item:', error);
      alert('Error creating shipping item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/shipping');
  };

  if (isSuccess) {
    return (
      <div className="success-container">
        <div className="checkmark"></div>
        <h2>Shipping ID created</h2>
        <button
          className="continue-button"
          onClick={() => navigate(`/shipping-item/${createdShippingID}`)}
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="create-container">
      <h2 style={{ textAlign: 'center' }}>Create Shipping Item</h2>

      {/* Added forklift image below the heading */}
      <img
        src={forkliftImg}
        alt="Forklift"
        style={{ display: 'block', margin: '0 auto 20px', width: '40%' }}
      />

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <form onSubmit={handleSave}>
        {/* New Order of Fields */}
        <div className="form-group">
          <label>Received Date:</label>
          <input
            type="date"
            value={rcvdDate}
            onChange={(e) => setRcvdDate(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label>Shipping ID:</label>
          <input
            type="text"
            value={shippingID}
            onChange={(e) => setShippingID(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label>Quantity In Lot:</label>
          <input
            type="number"
            value={qtyInLot}
            onChange={(e) => setQtyInLot(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label>Project Number:</label>
          <input
            type="text"
            value={projectNumber}
            onChange={(e) => setProjectNumber(e.target.value)}
            onBlur={fetchProjectDetails}
            required
            disabled={isLoading}
          />
          {isProjectLoading && <div className="small-spinner"></div>}
        </div>
        <div className="form-group">
          <label>Project Manager:</label>
          <input
            type="text"
            value={projectManager}
            readOnly
            required
            style={{
              backgroundColor: '#f9f9f9',
              cursor: 'not-allowed',
              border: '1px solid #ccc',
              padding: '0.5rem'
            }}
          />
        </div>
        <div className="form-group">
          <label>Client:</label>
          <input
            type="text"
            value={company}
            readOnly
            required
            style={{
              backgroundColor: '#f9f9f9',
              cursor: 'not-allowed',
              border: '1px solid #ccc',
              padding: '0.5rem'
            }}
          />
        </div>
        <div className="form-group">
          <label>Carrier:</label>
          <input
            type="text"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label>Shipper:</label>
          <input
            type="text"
            value={shipper}
            onChange={(e) => setShipper(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        {/* The rest of the fields remain as before */}
        <div className="form-group">
          <label>Description:</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label>Receiver:</label>
          <input
            type="text"
            value={receiver}
            readOnly
            required
            style={{
              backgroundColor: '#f9f9f9',
              cursor: 'not-allowed',
              border: '1px solid #ccc',
              padding: '0.5rem'
            }}
          />
        </div>

        {/* --- Shipping Photo Section --- */}
        <div className="form-group">
          <label>Photo of Item as Recieved:</label>
          {photo ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '1rem'
              }}
            >
              <img
                src={photo}
                alt="Captured"
                style={{ maxWidth: '200px', marginBottom: '0.5rem' }}
              />
              <button
                type="button"
                onClick={handleRetakePhoto}
                disabled={isLoading}
                className="styled-button"
              >
                Retake Photo
              </button>
            </div>
          ) : (
            <>
              {isMobile ? (
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  disabled={isLoading}
                  className="styled-button"
                >
                  Take Photo
                </button>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelected}
                  disabled={isLoading}
                />
              )}
            </>
          )}
          {isMobile && (
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={photoInputRef}
              style={{ display: 'none' }}
              onChange={handlePhotoSelected}
            />
          )}
        </div>

        {/* --- Manifest Document Section --- */}
        <div className="form-group">
          <label>Manifest Document:</label>
          {isMobile ? (
            scannedPages.length > 0 ? (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '10px'
                  }}
                >
                  {scannedPages.map((page, index) => (
                    <img
                      key={index}
                      src={page}
                      alt={`Scanned Page ${index + 1}`}
                      style={{
                        width: '100%',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '5px'
                      }}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleClearScannedPages}
                  disabled={isLoading}
                  className="styled-button"
                >
                  Clear Scanned Pages
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleScanDocument}
                disabled={isLoading}
                className="styled-button"
              >
                Scan Document
              </button>
            )
          ) : (
            <input
              type="file"
              accept="application/pdf"
              onChange={handlePDFManifestChange}
              disabled={isLoading}
            />
          )}
          {isMobile && (
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={scanInputRef}
              style={{ display: 'none' }}
              onChange={handleScanSelected}
            />
          )}
        </div>

        {/* --- Email Procurement Checkbox --- */}
        <div
          className="form-group"
          style={{
            display: 'inline-flex',
            alignItems: 'center'
          }}
        >
          <label htmlFor="emailProcurement" style={{ marginRight: '6px', marginBottom: 0 }}>
            Email Procurement
          </label>
          <input
            type="checkbox"
            id="emailProcurement"
            checked={emailProcurement}
            onChange={(e) => setEmailProcurement(e.target.checked)}
            disabled={isLoading}
          />
        </div>

        {/* --- Cropper Modal for Mobile Scanning --- */}
        {isMobile && showCropper && capturedScan && (
          <div className="scanner-modal">
            {isProcessingScan ? (
              <div className="loading-spinner">Processing...</div>
            ) : (
              <Cropper
                src={capturedScan}
                style={{ height: 400, width: '100%' }}
                guides={true}
                ref={cropperRef}
                viewMode={1}
                initialAspectRatio={8.5 / 11}
              />
            )}
            <div style={{ marginTop: '10px' }}>
              <button type="button" onClick={handleAddScannedPage} disabled={isLoading}>
                Add Scanned Page
              </button>
              <button type="button" onClick={handleDoneCropping} disabled={isLoading}>
                Done Cropping
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCropper(false);
                  setCapturedScan(null);
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="form-buttons">
          <button type="submit" disabled={isLoading}>
            Create Shipping Item
          </button>
          <button type="button" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateShippingItem;
